import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "../../../common/Modal";
import Button from "../../../common/Buttons";
import TextInput from "../../../common/TextInput";
import SelectInput from "../../../common/SelectInput";

import { fetchInventoryLookups } from "../../../redux/action/inventoryAction/InventoryAction";
import { LOCATIONS, PROJECT_DATA } from "../../../utils/Constant";

const EMPTY_FORM = {
  item: "",
  type: "",
  customType: "",
  catalog: "",
  manufacturer: "",
  supplier: "",
  location: "",
  project: "",
  customProject: "",
  quantity: "",
  numberOfSize: "",
  unitSize: "",
  addDate: new Date().toISOString().slice(0, 10),
  expireDate: "",
};

const toISODate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
};

export default function InventoryFormModal({
  open,
  mode,
  initialValue,
  onClose,
  onSubmit,
  loading,
}) {
  const dispatch = useDispatch();
  const inventoryState = useSelector((state) => state.inventory || {});
  const types = inventoryState.types || [];
  const suppliers = inventoryState.suppliers || [];
  const lookupsLoading = inventoryState.lookupsLoading || false;

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      dispatch(fetchInventoryLookups());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === "edit" && initialValue) {
      const initialType = initialValue.type || "";
      const initialProject = initialValue.project || "";
      const knownType = types.includes(initialType);
      const knownProject = PROJECT_DATA.some(
        (project) => project.name === initialProject,
      );
      const projectData = PROJECT_DATA.find(
        (project) => project.name === initialProject,
      );
      setForm({
        item: initialValue.item || "",
        type: knownType ? initialType : initialType ? "Custom" : "",
        customType: knownType ? "" : initialType,
        catalog: initialValue.catalog || "",
        manufacturer: initialValue.manufacturer || "",
        supplier: initialValue.supplier || "",
        location: initialValue.location || "",
        project: knownProject
          ? projectData?.id || ""
          : initialProject
            ? "custom"
            : "",
        customProject: knownProject ? "" : initialProject,
        quantity:
          initialValue.quantity !== undefined && initialValue.quantity !== null
            ? initialValue.quantity
            : "",
        addDate: toISODate(initialValue.addDate),
        expireDate: toISODate(initialValue.expireDate),
        numberOfSize: initialValue.numberOfSize ?? "",
        unitSize: initialValue.unitSize || "",
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        addDate: new Date().toISOString().slice(0, 10),
      });
    }
    setErrors({});
  }, [open, mode, initialValue, types]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: "" }));
  };

  const supplierOptions = suppliers
    .map((supplier) => {
      if (typeof supplier === "string") {
        return { value: supplier, label: supplier };
      }

      return { value: supplier?.name || "", label: supplier?.name || "" };
    })
    .filter((supplier) => supplier.value);

  const typeOptions = [...new Set(types.filter(Boolean).concat("Custom"))].map(
    (type) => ({ value: type, label: type }),
  );

  const locationOptions = (LOCATIONS || []).map((location) => ({
    value: location,
    label: location,
  }));

  const projectOptions = (PROJECT_DATA || []).map((project) => ({
    value: project.id,
    label: project.name,
  }));

  projectOptions.push({ value: "custom", label: "Custom" });

  const validateForm = () => {
    const validationErrors = {};

    if (!form.item.trim()) {
      validationErrors.item = "Item name is required";
    }

    if (!form.type) {
      validationErrors.type = "Type is required";
    }

    if (form.type === "Custom" && !form.customType.trim()) {
      validationErrors.customType = "Custom type is required";
    }

    if (!form.supplier) {
      validationErrors.supplier = "Supplier is required";
    }

    if (!form.location) {
      validationErrors.location = "Location is required";
    }

    if (form.project === "custom" && !form.customProject.trim()) {
      validationErrors.customProject = "Custom project name is required";
    }

    if (
      form.quantity === "" ||
      form.quantity === null ||
      form.quantity === undefined
    ) {
      validationErrors.quantity = "Quantity is required";
    } else if (Number(form.quantity) < 0) {
      validationErrors.quantity = "Quantity cannot be negative";
    }

    if (
      form.numberOfSize === "" ||
      form.numberOfSize === null ||
      form.numberOfSize === undefined ||
      Number(form.numberOfSize) <= 0
    ) {
      validationErrors.numberOfSize = "Number of size is required";
    }

    if (!form.unitSize.trim()) {
      validationErrors.unitSize = "Unit size is required";
    }
    if (!form.addDate) {
      validationErrors.addDate = "Add date is required";
    }
    if (!form.expireDate) {
      validationErrors.expireDate = "Expiry date is required";
    }
    if (form.addDate && form.expireDate && form.expireDate < form.addDate) {
      validationErrors.expireDate = "Expiry date cannot be before add date";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    const valid = validateForm();
    if (!valid) {
      return;
    }

    let finalType = form.type;

    if (form.type === "Custom") {
      finalType = form.customType.trim();
    }

    let finalProject = "";

    if (form.project === "custom") {
      finalProject = form.customProject.trim();
    } else if (form.project) {
      const selectedProject = PROJECT_DATA.find(
        (project) => project.id === form.project,
      );
      finalProject = selectedProject?.name || "";
    }

    const payload = {
      item: form.item.trim(),
      type: finalType,
      catalog: form.catalog.trim(),
      manufacturer: form.manufacturer.trim(),
      supplier: form.supplier,
      location: form.location,
      project: finalProject,
      quantity: Number(form.quantity),
      addDate: form.addDate,
      expireDate: form.expireDate,
      numberOfSize: Number(form.numberOfSize),
      unitSize: form.unitSize.trim(),
    };
    onSubmit(payload);
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={onClose} disabled={loading}>
        Cancel
      </Button>

      <Button variant="solid" loading={loading} onClick={handleSubmit}>
        {mode === "edit" ? "Save Changes" : "Add Item"}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Inventory Item" : "Add Inventory Item"}
      footer={footer}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextInput
            label="Item Name"
            required
            value={form.item}
            onChange={handleChange("item")}
            placeholder="e.g. Nitrile Gloves"
          />
          {errors.item && (
            <p className="mt-1 text-xs text-red-500">{errors.item}</p>
          )}
        </div>

        <SelectInput
          name="type"
          label="Type"
          required
          value={form.type}
          onChange={handleChange("type")}
          options={typeOptions}
          placeholder={lookupsLoading ? "Loading types..." : "Select type"}
          disabled={lookupsLoading}
          error={errors.type}
        />

        {form.type === "Custom" && (
          <div>
            <TextInput
              label="Custom Type"
              required
              value={form.customType}
              onChange={handleChange("customType")}
              placeholder="Enter custom type"
            />

            {errors.customType && (
              <p className="mt-1 text-xs text-red-500">{errors.customType}</p>
            )}
          </div>
        )}

        <TextInput
          label="Catalog"
          value={form.catalog}
          onChange={handleChange("catalog")}
          placeholder="Catalog / batch #"
        />

        <TextInput
          label="Manufacturer"
          value={form.manufacturer}
          onChange={handleChange("manufacturer")}
          placeholder="Manufacturer"
        />

        <SelectInput
          name="supplier"
          label="Supplier"
          required
          value={form.supplier}
          onChange={handleChange("supplier")}
          options={supplierOptions}
          placeholder={
            lookupsLoading ? "Loading suppliers..." : "Select supplier"
          }
          disabled={lookupsLoading}
          error={errors.supplier}
        />

        <SelectInput
          name="location"
          label="Location"
          required
          value={form.location}
          onChange={handleChange("location")}
          options={locationOptions}
          placeholder="Select location"
          error={errors.location}
        />

        <SelectInput
          name="project"
          label="Project"
          value={form.project}
          onChange={handleChange("project")}
          options={projectOptions}
          placeholder="Select project"
          error={errors.project}
        />

        {form.project === "custom" && (
          <div>
            <TextInput
              label="Custom Project Name"
              required
              value={form.customProject}
              onChange={handleChange("customProject")}
              placeholder="Enter project name"
            />

            {errors.customProject && (
              <p className="mt-1 text-xs text-red-500">
                {" "}
                {errors.customProject}{" "}
              </p>
            )}
          </div>
        )}

        <div>
          <TextInput
            label="Quantity"
            required
            type="number"
            value={form.quantity}
            onChange={handleChange("quantity")}
            placeholder="0"
          />

          {errors.quantity && (
            <p className="mt-1 text-xs text-red-500">{errors.quantity}</p>
          )}
        </div>

        <div>
          <TextInput
            label="Number of Size"
            required
            type="number"
            value={form.numberOfSize}
            onChange={handleChange("numberOfSize")}
            placeholder="Enter number of size"
          />

          {errors.numberOfSize && (
            <p className="mt-1 text-xs text-red-500">{errors.numberOfSize}</p>
          )}
        </div>

        <div>
          <TextInput
            label="Unit Size"
            required
            value={form.unitSize}
            onChange={handleChange("unitSize")}
            placeholder="e.g. Box, Piece, Kg, Liter"
          />

          {errors.unitSize && (
            <p className="mt-1 text-xs text-red-500">{errors.unitSize}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="addDate"
            className="mb-1 block text-[12px] font-medium text-slate-600"
          >
            Add Date
            <span className="ml-0.5 text-red-500">*</span>
          </label>

          <input
            id="addDate"
            name="addDate"
            type="date"
            value={form.addDate}
            onChange={handleChange("addDate")}
            className={`w-full rounded-lg border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-brand-500/20 ${
              errors.addDate
                ? "border-red-500 focus:border-red-500"
                : "border-slate-200 focus:border-brand-500"
            }`}
          />

          {errors.addDate && (
            <p className="mt-1 text-xs text-red-500">{errors.addDate}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="expireDate"
            className="mb-1 block text-[12px] font-medium text-slate-600"
          >
            Expiry Date
            <span className="ml-0.5 text-red-500">*</span>
          </label>

          <input
            id="expireDate"
            name="expireDate"
            type="date"
            value={form.expireDate}
            onChange={handleChange("expireDate")}
            className={`w-full rounded-lg border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-brand-500/20 ${
              errors.expireDate
                ? "border-red-500 focus:border-red-500"
                : "border-slate-200 focus:border-brand-500"
            }`}
          />
          {errors.expireDate && (
            <p className="mt-1 text-xs text-red-500">{errors.expireDate}</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
